import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import cogoToast from "cogo-toast";

import { SectionLoader } from "../../../../../components/PageLoader";
import { uploadFile } from "../../../../../utils/helpers";

import {
  createFeedAction,
  getFeedsAction,
  createCommentPostAction,
  deleteFeedsAction,
  deleteCommentAction,
  toggleFeedLikeAction,
  fetchPostCommentsAction,
} from "../../../../../redux/actions/feedAction";

import ShareButton from "../ShareButton";
import "../feedTabs.css";
import "./communityTab.css";

/**
 * CommunityTab — the user-generated social feed (modern social design).
 *
 * Contains:
 *   - Post composer (text + image upload with preview)
 *   - Post list with author avatar, timestamp, content, image grid
 *   - Engagement bar (like, comment, share)
 *   - Comments (expand/collapse, lazy-loaded, add, delete)
 *   - Delete own post
 */
const CommunityTab = () => {
  const dispatch = useDispatch();
  const isMounted = useRef(true);

  const uploadInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [deletingFeedId, setDeletingFeedId] = useState(null);
  const [likingFeedId, setLikingFeedId] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const get_feed_result = useSelector((store) => store.get_feed_result);
  const { userData } = useSelector((store) => store.auth);

  const [postContent, setPostContent] = useState("");
  const [postImage, setPostImage] = useState("");

  const [stateComment, setCommentState] = useState({
    feed_id: "",
    content: "",
  });

  useEffect(() => {
    isMounted.current = true;
    const fetchFeeds = async () => {
      try {
        setLoading(true);
        await getFeedsAction(dispatch, 1);
      } catch (error) {
        console.error("Error fetching feeds:", error);
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };
    fetchFeeds();
    return () => {
      isMounted.current = false;
    };
  }, [dispatch]);

  // ── Image upload ──
  const handleChangeUpload = ({ target: { files } }) => {
    if (files && files[0]) {
      const file = files[0];
      // Show local preview immediately
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);

      setIsUploading(true);
      Promise.resolve(
        uploadFile(
          file,
          "https://api.cloudinary.com/v1_1/workbrook-hash/raw/upload"
        )
      )
        .then(({ data, success }) => {
          if (success) {
            setPostImage(data);
          } else {
            cogoToast.error("Unsuccessful image upload", {
              hideAfter: 4,
              position: "top-center",
            });
            setImagePreview("");
          }
        })
        .finally(() => {
          setIsUploading(false);
        });
    }
  };

  const removeImage = () => {
    setPostImage("");
    setImagePreview("");
    if (uploadInputRef.current) uploadInputRef.current.value = "";
  };

  // ── Submit post ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!postContent.trim()) {
      cogoToast.error("Please enter some content for your post", {
        hideAfter: 3,
        position: "top-center",
      });
      return;
    }

    try {
      if (isMounted.current) setLoading(true);

      const response = await createFeedAction(dispatch, {
        content: postContent,
        img_url: postImage,
      });

      if (response?.success && isMounted.current) {
        setPostContent("");
        setPostImage("");
        setImagePreview("");
        if (uploadInputRef.current) uploadInputRef.current.value = "";
      }
    } catch (error) {
      if (isMounted.current) {
        console.error("Error creating feed:", error);
        cogoToast.error(error.message || "Failed to create post", {
          hideAfter: 4,
          position: "top-center",
        });
      }
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  // ── Comments ──
  const handleSubmitComment = async (e, feedId) => {
    e.preventDefault();
    e.stopPropagation();

    if (commentLoading) return;

    if (!stateComment.content.trim()) {
      cogoToast.error("Please enter a comment", {
        hideAfter: 3,
        position: "top-center",
      });
      return;
    }

    try {
      setCommentLoading(true);
      const actualFeedId = feedId || stateComment.feed_id;

      const response = await createCommentPostAction(dispatch, {
        ...stateComment,
        feed_id: actualFeedId,
      });

      if (response?.success && isMounted.current) {
        setCommentState({ feed_id: "", content: "" });
        setExpandedPostId(actualFeedId);
      }
    } catch (error) {
      if (isMounted.current) console.error("Error adding comment:", error);
    } finally {
      if (isMounted.current) setCommentLoading(false);
    }
  };

  const deleteFeedSubmit = async (_id) => {
    if (deletingFeedId) return;
    try {
      setDeletingFeedId(_id);
      await deleteFeedsAction(dispatch, { item_id: _id });
    } catch (error) {
      console.error("Error deleting feed:", error);
    } finally {
      if (isMounted.current) setDeletingFeedId(null);
    }
  };

  const deleteCommentSubmit = async (commentId, feedId) => {
    if (deletingCommentId) return;
    try {
      setDeletingCommentId(commentId);
      const response = await deleteCommentAction(dispatch, {
        comment_id: commentId,
        feed_id: feedId,
      });
      if (response?.success && isMounted.current) {
        setExpandedPostId(feedId);
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    } finally {
      if (isMounted.current) setDeletingCommentId(null);
    }
  };

  const handleToggleLike = async (feedId, likedByMe) => {
    if (likingFeedId) return;
    try {
      setLikingFeedId(feedId);
      await toggleFeedLikeAction(dispatch, feedId, likedByMe);
    } catch (error) {
      console.error("Error toggling like:", error);
    } finally {
      if (isMounted.current) setLikingFeedId(null);
    }
  };

  const [loadedCommentIds, setLoadedCommentIds] = useState({});
  const [loadingCommentsFor, setLoadingCommentsFor] = useState(null);

  const handleToggleComments = async (postId) => {
    const nextExpanded = expandedPostId === postId ? null : postId;
    setExpandedPostId(nextExpanded);

    if (nextExpanded && !loadedCommentIds[postId]) {
      try {
        setLoadingCommentsFor(postId);
        await fetchPostCommentsAction(dispatch, postId);
        setLoadedCommentIds((prev) => ({ ...prev, [postId]: true }));
      } catch (error) {
        console.error("Error fetching comments:", error);
      } finally {
        setLoadingCommentsFor(null);
      }
    }
  };

  const feedsArray =
    get_feed_result &&
    Array.isArray(get_feed_result.get_feed_result)
      ? get_feed_result.get_feed_result
      : Array.isArray(get_feed_result)
      ? get_feed_result
      : [];

  const myAvatar =
    userData?.avatar ||
    "https://res.cloudinary.com/workbrook-hash/image/upload/v1587164870/App/user-circle-02_jbpaez.png";
  const myName =
    userData?.first_name || userData?.last_name
      ? `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim()
      : "You";

  return (
    <div className="community-tab">
      {/* ── Post Composer ── */}
      <div className="community-composer">
        <div className="community-composer-header">
          <img src={myAvatar} alt="" className="community-avatar-sm" />
          <span className="community-composer-prompt">Share something with the community...</span>
        </div>

        <form onSubmit={handleSubmit}>
          <textarea
            className="community-composer-textarea"
            placeholder="What's on your mind? Ask a question, share a win, or start a conversation..."
            value={postContent}
            onChange={(e) => setPostContent(e.target.value)}
            rows={3}
          />

          {/* Image preview */}
          {imagePreview && (
            <div className="community-composer-preview">
              <img src={imagePreview} alt="Preview" />
              <button type="button" onClick={removeImage} className="community-preview-remove">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              {isUploading && (
                <div className="community-preview-uploading">
                  <div className="community-spinner" />
                  <span>Uploading...</span>
                </div>
              )}
            </div>
          )}

          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*"
            onChange={handleChangeUpload}
            style={{ display: "none" }}
          />

          <div className="community-composer-actions">
            <button
              type="button"
              className="community-composer-attach"
              onClick={() => uploadInputRef.current?.click()}
              disabled={isUploading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <span>Photo</span>
            </button>

            <button
              type="submit"
              className="community-composer-post"
              disabled={loading || isUploading || !postContent.trim()}
            >
              {loading ? (
                <>
                  <div className="community-spinner community-spinner-sm" />
                  Posting...
                </>
              ) : (
                "Post"
              )}
            </button>
          </div>
        </form>
      </div>

      {loading && feedsArray.length === 0 && <SectionLoader />}

      {/* ── Empty state ── */}
      {!loading && feedsArray.length === 0 && (
        <div className="community-empty">
          <div className="community-empty-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h3>Be the first to share something</h3>
          <p>Ask a question, celebrate a win, or start a conversation.</p>
        </div>
      )}

      {/* ── Post List ── */}
      <div className="community-posts">
        {feedsArray.map((item, index) => {
          const images = Array.isArray(item.image_urls) ? item.image_urls : (item.img_url ? [item.img_url] : []);
          const isOwnPost = userData?.id && item.author_id == userData.id;

          return (
            <div className="community-post-card" key={item.id || index}>
              {/* Post header */}
              <div className="community-post-header">
                <img
                  src={item.author_avatar || "https://res.cloudinary.com/workbrook-hash/image/upload/v1587164870/App/user-circle-02_jbpaez.png"}
                  alt=""
                  className="community-avatar-md"
                />
                <div className="community-post-meta">
                  <span className="community-post-author">{item.author_name || "User"}</span>
                  <span className="community-post-time">{item.created_at}</span>
                </div>
                {isOwnPost && (
                  <button
                    type="button"
                    onClick={() => deleteFeedSubmit(item.id)}
                    disabled={deletingFeedId === item.id}
                    className="community-post-delete"
                    title="Delete post"
                  >
                    {deletingFeedId === item.id ? (
                      <div className="community-spinner community-spinner-sm" />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    )}
                  </button>
                )}
              </div>

              {/* Post body */}
              {item.body && <p className="community-post-body">{item.body}</p>}

              {/* Post images */}
              {images.length > 0 && (
                <div className={`community-post-images community-post-images-${Math.min(images.length, 4)}`}>
                  {images.slice(0, 4).map((url, imgIdx) => (
                    <img key={imgIdx} src={url} alt="" className="community-post-image" />
                  ))}
                </div>
              )}

              {/* Engagement bar */}
              <div className="community-engagement-bar">
                <button
                  type="button"
                  onClick={() => handleToggleLike(item.id, item.liked_by_me)}
                  disabled={likingFeedId === item.id}
                  className={`community-engagement-btn ${item.liked_by_me ? "community-liked" : ""}`}
                >
                  <svg
                    width="20" height="20" viewBox="0 0 24 24"
                    fill={item.liked_by_me ? "currentColor" : "none"}
                    stroke="currentColor" strokeWidth="2"
                    className={item.liked_by_me ? "community-heart-pop" : ""}
                  >
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                  <span>{item.like_count > 0 ? item.like_count : "Like"}</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleToggleComments(item.id)}
                  className={`community-engagement-btn ${expandedPostId === item.id ? "community-engagement-active" : ""}`}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span>{item.comment_count > 0 ? item.comment_count : "Comment"}</span>
                </button>

                <ShareButton
                  url={`${window.location.origin}/community?post=${item.id}`}
                  title="Workbrook Community Post"
                  text={item.body ? item.body.substring(0, 100) : "Check out this post on Workbrook"}
                  className="community-engagement-btn"
                />
              </div>

              {/* Comments panel */}
              {expandedPostId === item.id && (
                <div className="community-comments-panel">
                  {/* Comment input */}
                  <form
                    onSubmit={(e) => handleSubmitComment(e, item.id)}
                    className="community-comment-form"
                  >
                    <img src={myAvatar} alt="" className="community-avatar-xs" />
                    <input
                      type="text"
                      onChange={({ target: { value } }) => {
                        setCommentState({ ...stateComment, feed_id: item.id, content: value });
                      }}
                      value={stateComment.feed_id === item.id ? stateComment.content : ""}
                      placeholder="Write a comment..."
                      disabled={commentLoading}
                      className="community-comment-input"
                    />
                    <button
                      type="submit"
                      disabled={commentLoading || !(stateComment.feed_id === item.id && stateComment.content.trim())}
                      className="community-comment-submit"
                    >
                      {commentLoading ? (
                        <div className="community-spinner community-spinner-sm" />
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="22" y1="2" x2="11" y2="13" />
                          <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                      )}
                    </button>
                  </form>

                  {/* Comments list */}
                  {loadingCommentsFor === item.id && (
                    <div className="community-comments-loading">Loading comments...</div>
                  )}

                  {loadingCommentsFor !== item.id && item.comments && item.comments.length > 0 && (
                    <div className="community-comments-list">
                      {item.comments.map((comment, commentIndex) => (
                        <div key={comment.id || commentIndex} className="community-comment">
                          <img
                            src={comment.avatar || "https://res.cloudinary.com/workbrook-hash/image/upload/v1587164870/App/user-circle-02_jbpaez.png"}
                            alt=""
                            className="community-avatar-xs"
                          />
                          <div className="community-comment-body">
                            <div className="community-comment-bubble">
                              <span className="community-comment-author">{comment.author_name || "User"}</span>
                              <p className="community-comment-text">{comment.body}</p>
                            </div>
                            {userData?.id && comment.user_id == userData.id && (
                              <button
                                type="button"
                                onClick={() => deleteCommentSubmit(comment.id, item.id)}
                                disabled={deletingCommentId === comment.id}
                                className="community-comment-delete"
                              >
                                {deletingCommentId === comment.id ? "..." : "Delete"}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {loadingCommentsFor !== item.id && (!item.comments || item.comments.length === 0) && (
                    <div className="community-comments-empty">No comments yet. Be the first to comment!</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CommunityTab;
